import { MotionDiv } from '@/components/element/MotionDiv';
import { useSalesOrderInvalidate } from '@/hooks/api/1260_sales_order';
import { useSalesOrderContractTaskPhases } from '@/hooks/api/1610_sales_order_contract_task_phases';
import { useSalesOrderContractTasksInvalidate } from '@/hooks/api/1620_sales_order_contract_tasks';
import { useSalesOrderContractTaskInvalidate } from '@/hooks/api/1660_sales_order_contract_task';
import { useMoveSalesOrderContractTaskPhaseToNext } from '@/hooks/api/1670_move_to_next_phase';
import { useMoveSalesOrderContractTaskPhaseToPrev } from '@/hooks/api/1671_move_to_prev_phase';
import { SalesOrderContractTaskPhase } from '@/hooks/api/models';
import { toastApiError } from '@/lib/toast';
import { useLoading } from '@/providers/LoadingProvider';
import { useCurrentSalesOrderContractTask } from '@/providers/SalesOrderContractTaskProvider';
import { useCurrentTeamMember } from '@/providers/TeamMemberProvider';
import { Fragment, RefObject, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useUpdateSalesOrderPhase } from '@/hooks/api/1270_update_sales_order_phase';
import { ModalMessageConfirm } from '@/components/element/modal/ModalMessageConfirm';
import { useTeamSalesOrderPhases } from '@/hooks/api/0250_team_sales_order_phases';

export const TaskProgress = () => {
  const { setShowLoading } = useLoading();
  const { _w_, _t_ } = useCurrentTeamMember();
  const { salesOrderContractTask: task } = useCurrentSalesOrderContractTask();
  const { data: phases } = useSalesOrderContractTaskPhases();
  const [currentPhase, setCurrentPhase] = useState(task!.sales_order_contract_task_phase);
  const { data: teamSalesOrderPhases } = useTeamSalesOrderPhases({ identifier: _w_!.identifier, team_id: _t_!.id });
  const { invalidate: invalidateSalesOrder } = useSalesOrderInvalidate({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    id: task!.sales_order.id,
  });
  const { invalidate: invalidateTasks } = useSalesOrderContractTasksInvalidate(_w_!.identifier);
  const showMessageButtonRef = useRef<HTMLButtonElement>(null);

  const {
    mutate: updateSalesOrderPhaseMutate,
    data: salesOrder,
    isPending: isUpdateSalesOrderPhasePending,
  } = useUpdateSalesOrderPhase();

  useEffect(() => {
    setCurrentPhase(task!.sales_order_contract_task_phase);
    invalidateSalesOrder();
    invalidateTasks();
  }, [task]);

  // 確認ダイアログで「変更する」クリック時、SalesOrderのフェーズを契約中に変更する
  const handleUpdateOrderPhaseClick = () => {
    updateSalesOrderPhaseMutate({
      identifier: _w_!.identifier,
      team_id: _t_!.id,
      sales_order_id: task!.sales_order.id,
      team_sales_order_phase_id: Number(teamSalesOrderPhases!.find((p) => p.seq == 5)!.id),
    });
  };

  useEffect(() => setShowLoading(isUpdateSalesOrderPhasePending), [isUpdateSalesOrderPhasePending]);

  useEffect(() => {
    if (salesOrder) {
      invalidateSalesOrder();
    }
  }, [salesOrder]);

  if (phases == null) return <></>;

  return (
    <MotionDiv className="card mb-4">
      <div className="card-header d-flex align-items-center justify-content-between">
        <div>
          <h6 className="mb-0">タスク進捗状況</h6>
        </div>
      </div>
      <div className="card-body pt-0">
        <ModalMessageConfirm
          modalId="messageModal"
          message={'契約が承認されました。\nプロジェクトのフェーズを契約中に変更しますか？'}
          messageLink={{
            url: `/w/${_w_!.identifier}/teams/${_t_!.id}/sales_orders/${task!.sales_order.id}`,
            label: `${task!.sales_order.name}`,
          }}
          showModalButtonRef={showMessageButtonRef}
          okClicked={handleUpdateOrderPhaseClick}
          okButtonLabel={'変更する'}
          cancelButtonLabel={'変更しない'}
        />

        {phases.map((phase, i) => (
          <Fragment key={`soctp-${phase.id}`}>
            {i != 0 && <hr className="horizontal dark" />}
            <div className="d-flex align-items-center justify-content-between">
              <div className="text-sm text-dark font-weight-bold">{phase.label}</div>
              <div className="d-flex justify-content-end align-items-center">
                {phase.id == currentPhase.id && (
                  <img src={task!.user.user_account.icon_file_url} className="avatar avatar-sm rounded-circle" />
                )}
                {phase.id < currentPhase.id && <span className="badge badge-success">OK</span>}
                <MoveToPrevPhaseButton
                  phase={phase}
                  curPhase={currentPhase}
                  phases={phases}
                  showMessageButtonRef={showMessageButtonRef}
                />
                <MoveToNextPhaseButton
                  phase={phase}
                  curPhase={currentPhase}
                  phases={phases}
                  showMessageButtonRef={showMessageButtonRef}
                />
              </div>
            </div>
          </Fragment>
        ))}
      </div>
    </MotionDiv>
  );
};

type ButtonProps = {
  phase: SalesOrderContractTaskPhase;
  curPhase: SalesOrderContractTaskPhase;
  phases: SalesOrderContractTaskPhase[];
  showMessageButtonRef: RefObject<HTMLButtonElement>;
};

// 次のフェーズボタン
const MoveToNextPhaseButton = ({ phase, curPhase, phases, showMessageButtonRef }: ButtonProps) => {
  const { setShowLoading } = useLoading();
  const { _w_, _t_, isApprover } = useCurrentTeamMember();
  const { sales_order_id } = useParams();
  const { salesOrderContractTask: task } = useCurrentSalesOrderContractTask();
  const { mutate, data, isPending, isSuccess, error } = useMoveSalesOrderContractTaskPhaseToNext();
  const { invalidate: invalidateTask } = useSalesOrderContractTaskInvalidate({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    sales_order_id: Number(sales_order_id),
    id: task!.id,
  });

  useEffect(() => setShowLoading(isPending), [isPending]);
  useEffect(() => toastApiError(error), [error]);
  useEffect(() => {
    if (isSuccess && data) {
      invalidateTask();

      // NDA以外かつ承認済み以降のフェーズの場合に確認ダイアログを表示
      if (task!.contract_context.id != 2 && data.sales_order.team_sales_order_phase.seq < 5) {
        if (
          data.sales_order_contract_task_phase.name == 'approved' ||
          data.sales_order_contract_task_phase.name == 'completed'
        ) {
          showMessageButtonRef.current?.click();
        }
      }
    }
  }, [isSuccess]);

  const onClick = () => {
    mutate({
      identifier: _w_!.identifier,
      team_id: _t_!.id,
      sales_order_id: Number(sales_order_id),
      task_id: task!.id,
      current_sales_order_contract_task_phase_id: curPhase.id,
    });
  };

  if (phase.id != curPhase.id) return <></>;
  if (phase.id == phases.at(-1)!.id) return <></>;

  return (
    <button
      className={`btn btn-icon-only bg-gradient-success ms-1 ${
        phase.name == 'request_approval' && !isApprover ? 'disabled' : ''
      }`}
      onClick={onClick}
    >
      <i className="fas fa-arrow-down"></i>
    </button>
  );
};

// 前のフェーズボタン
const MoveToPrevPhaseButton = ({ phase, curPhase, phases }: ButtonProps) => {
  const { setShowLoading } = useLoading();
  const { _w_, _t_, isApprover } = useCurrentTeamMember();
  const { sales_order_id } = useParams();
  const { salesOrderContractTask: task } = useCurrentSalesOrderContractTask();
  const { mutate, data, isPending, isSuccess, error } = useMoveSalesOrderContractTaskPhaseToPrev();
  const { invalidate: invalidateTask } = useSalesOrderContractTaskInvalidate({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    sales_order_id: Number(sales_order_id),
    id: task!.id,
  });

  useEffect(() => setShowLoading(isPending), [isPending]);
  useEffect(() => toastApiError(error), [error]);

  useEffect(() => {
    if (isSuccess && data) invalidateTask();
  }, [isSuccess]);

  const onClick = () => {
    mutate({
      identifier: _w_!.identifier,
      team_id: _t_!.id,
      sales_order_id: Number(sales_order_id),
      task_id: task!.id,
      current_sales_order_contract_task_phase_id: curPhase.id,
    });
  };

  if (phase.id != curPhase.id) return <></>;
  if (phase.id == phases[0].id) return <></>;

  return (
    <button className="btn btn-icon-only bg-gradient-warning ms-1" onClick={onClick}>
      <i className="fas fa-arrow-up"></i>
    </button>
  );
};
